import { __assign } from "tslib";
import { createTheme, FontSizes } from '@fluentui/react';
import { DefaultPalette, Depths } from '@fluentui/theme';
import { getFluentStyleOverrides } from '../fluentStyles/FluentStyleOverrides';
import { fluent2ComponentStyles, Fluent2WebDarkTheme, Fluent2WebLightTheme } from '@fluentui/fluent2-theme';
import { M365Fluent2DarkTheme, M365Fluent2LightTheme } from '@m365-admin/fluent2-theme';
var v9LightPalette = __assign({}, Fluent2WebLightTheme.palette);
var v9DarkPalette = __assign({}, Fluent2WebDarkTheme.palette);
var extendedSemanticColorsLight = {
    alertDoNotDisturb: '#bc2f32',
    alertBackground: '#FAD5D7',
    alertForeground: '#7B0000',
    warningCaution: '#da3b01',
    warningCautionBackground: '#FFDAC3',
    warningCautionForeground: '#850D00',
    warningAway: v9LightPalette.yellow,
    warningAwayBackground: '#FFF2CA',
    warningAwayForeground: '#824F00',
    positivePrimary: '#107c10',
    positiveBackground: '#CFE8D0',
    positiveForeground: '#002C00',
    positiveSecondary: '#004b1c',
    complete: '#004e8c',
    completeBackground: '#D8EAF7',
    completeForeground: '#00326B'
};
var extendedSemanticColorsDark = {
    alertDoNotDisturb: '#e37d80',
    alertBackground: '#FAD5D7',
    alertForeground: '#7B0000',
    warningCaution: '#e36537',
    warningCautionBackground: '#FFDAC3',
    warningCautionForeground: '#850D00',
    warningAway: v9LightPalette.yellow,
    warningAwayBackground: '#FFF2CA',
    warningAwayForeground: '#824F00',
    positivePrimary: '#107c10',
    positiveBackground: '#CFE8D0',
    positiveForeground: '#002C00',
    positiveSecondary: '#004b1c',
    complete: '#286ea8',
    completeBackground: '#D8EAF7',
    completeForeground: '#00326B'
};
export var CoherenceNeutralHeaderPalette = {
    headerBackground: DefaultPalette.neutralPrimary,
    searchBoxBackground: DefaultPalette.neutralSecondary,
    searchBoxBackgroundHovered: '#757575',
    searchBoxPlaceholder: DefaultPalette.white,
    searchBoxText: DefaultPalette.white,
    buttonIcon: DefaultPalette.neutralTertiaryAlt,
    buttonBackgroundHovered: DefaultPalette.neutralSecondary,
    notificationBadgeBackground: DefaultPalette.themePrimary,
    notificationBadgeBackgroundHovered: DefaultPalette.themeDark,
    notificationBadgeText: DefaultPalette.white
};
export var CoherenceV9LightTheme = createTheme(__assign(__assign({}, Fluent2WebLightTheme), { palette: v9LightPalette, semanticColors: __assign(__assign(__assign(__assign({}, Fluent2WebLightTheme.semanticColors), M365Fluent2LightTheme.semanticColors), extendedSemanticColorsLight), { cardStandoutBackground: v9LightPalette.white }), components: __assign(__assign({}, getFluentStyleOverrides), fluent2ComponentStyles) }));
export var CoherenceV9DarkTheme = __assign(__assign({}, createTheme(__assign(__assign({}, Fluent2WebDarkTheme), { palette: v9DarkPalette, semanticColors: __assign(__assign(__assign(__assign({}, Fluent2WebDarkTheme.semanticColors), M365Fluent2DarkTheme.semanticColors), extendedSemanticColorsDark), { cardStandoutBackground: v9DarkPalette.neutralLighter }), components: __assign(__assign({}, getFluentStyleOverrides), fluent2ComponentStyles) }))), { neutralHeaderEnabled: true });
var v8Palette = __assign(__assign({}, DefaultPalette), { purpleLight: '#F1E6F9', purpleDark: '#4E1E6E', magentaLight: '#FCDDF1', magentaDark: '#790857', roseLight: '#FFDEE1', roseDark: '#820015', blueLight: '#D7EBFF', blueDark: '#00348D', tealLight: '#C7EEF1', tealDark: '#00414E', sandLight: '#F4EACA', sandDark: '#583100', indigoLight: '#E5EAFF', indigoDark: '#033283' });
var extendedSemanticColors = {
    headerText: v8Palette.neutralPrimary,
    navBackground: v8Palette.neutralQuaternaryAlt,
    navItemBackgroundHovered: v8Palette.neutralQuaternary,
    navItemBackgroundPressed: v8Palette.neutralTertiaryAlt,
    stepCompleted: v8Palette.themeDark,
    stepCurrent: v8Palette.themePrimary,
    stepNotStarted: v8Palette.neutralDark,
    stepModifierBorder: v8Palette.neutralDark,
    stepHover: v8Palette.themeDark,
    stepPressed: v8Palette.themeDarker,
    stepError: v8Palette.red,
    allStepsComplete: v8Palette.themePrimary,
    panelBackground: v8Palette.neutralLighter,
    bodyStandoutBackground: v8Palette.neutralLighter,
    bodyTextChecked: v8Palette.neutralDark,
    disabledBodyText: v8Palette.neutralTertiaryAlt,
    variantBorder: v8Palette.neutralSecondary,
    variantBorderHovered: v8Palette.neutralSecondary,
    defaultStateBackground: v8Palette.white,
    link: v8Palette.themeDark,
    linkHovered: v8Palette.themeDarker,
    buttonBackgroundChecked: v8Palette.neutralLight,
    buttonBackgroundCheckedHovered: v8Palette.neutralQuaternary,
    buttonBorder: v8Palette.neutralSecondary,
    buttonTextHovered: v8Palette.neutralPrimary,
    buttonTextChecked: v8Palette.neutralPrimary,
    buttonTextCheckedHovered: v8Palette.neutralPrimary,
    buttonTextPressed: v8Palette.neutralPrimary,
    buttonTextDisabled: v8Palette.neutralTertiaryAlt,
    primaryButtonTextDisabled: v8Palette.neutralTertiaryAlt,
    accentButtonBackground: v8Palette.themePrimary,
    inputBorder: v8Palette.neutralSecondary,
    inputFocusBorderAlt: v8Palette.themeDarkAlt,
    inputTextHovered: v8Palette.neutralPrimary,
    disabledText: v8Palette.neutralTertiaryAlt,
    disabledSubtext: v8Palette.neutralTertiaryAlt,
    listItemBackgroundCheckedHovered: v8Palette.neutralQuaternary,
    menuDivider: v8Palette.neutralQuaternaryAlt,
    menuIcon: v8Palette.neutralPrimary,
    menuHeader: v8Palette.neutralPrimary,
    menuItemTextHovered: v8Palette.neutralPrimary,
    alertDoNotDisturb: '#C5262C',
    alertBackground: '#FAD5D7',
    alertForeground: '#7B0000',
    warningCaution: '#F87528',
    warningAway: v8Palette.yellow,
    warningAwayBackground: '#FFF2CA',
    warningAwayForeground: '#824F00',
    positivePrimary: '#41A512',
    positiveBackground: '#CFE8D0',
    positiveForeground: '#002C00',
    positiveSecondary: '#085108',
    complete: v8Palette.blueMid,
    completeBackground: '#D8EAF7',
    completeForeground: '#00326B',
    tagBackgroundInsight: '#C8D1FA',
    tagBackgroundLowImpact: '#F0F0F0',
    tagBackgroundMediumImpact: v8Palette.neutralQuaternary,
    tagBackgroundHighImpact: '#292929',
    tagBackgroundNew: v8Palette.themePrimary,
    tagBackgroundActiveWarning: '#D13438',
    tagBackgroundRecommendation: '#A6E9ED',
    tagTextInsight: v8Palette.neutralPrimary,
    tagTextLowImpact: v8Palette.neutralPrimary,
    tagTextMediumImpact: v8Palette.black,
    tagTextHighImpact: v8Palette.white,
    tagTextNew: v8Palette.white,
    tagTextActiveWarning: v8Palette.white,
    alwaysWhite: '#ffffff',
    alwaysBlack: '#000000'
};
export var CoherenceTheme = createTheme({
    palette: v8Palette,
    semanticColors: extendedSemanticColors,
    effects: {
        roundedCorner2: '2px',
        elevation4: Depths.depth4,
        elevation8: Depths.depth8,
        elevation16: Depths.depth16,
        elevation64: Depths.depth64
    },
    components: getFluentStyleOverrides,
    fonts: {
        medium: {
            fontSize: FontSizes.medium
        },
        mediumPlus: {
            fontSize: FontSizes.mediumPlus
        }
    }
});
export default CoherenceTheme;
export var CoherenceV8Theme = CoherenceTheme;
//# sourceMappingURL=CoherenceTheme.js.map